import axios from 'axios';

import { HOST_API } from 'src/config-global';

// ----------------------------------------------------------------------

const axiosInstance = axios.create({ baseURL: HOST_API });

axiosInstance.interceptors.response.use(
  (res) => res,
  (error) => Promise.reject((error.response && error.response.data) || 'Something went wrong')
);

export default axiosInstance;

// ----------------------------------------------------------------------

export const fetcher = async (args) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosInstance.get(url, { ...config });

  return res.data;
};

// ----------------------------------------------------------------------

export const endpoints = {
  chat: '/api/chat',
  kanban: '/api/kanban',
  calendar: '/api/calendar',
  auth: {
    me: '/login/recuperarSesion',
    login: '/login',
    register: '/users/users',
    forgotPassword: '/login/recuperarContrasena',
    catalogs: '/users/users/catalogs',
    logout: (usuario) => `/login/${usuario}`,
  },
  mail: {
    list: '/api/mail/list',
    details: '/api/mail/details',
    labels: '/api/mail/labels',
  },
  post: {
    list: '/api/post/list',
    details: '/api/post/details',
    latest: '/api/post/latest',
    search: '/api/post/search',
  },
  product: {
    list: '/administracion/productos',
    create: '/administracion/productos',
    update: '/administracion/productos',
    delete: '/administracion/productos',
  },
  settingFel: {
    list: '/administracion/configuracionFel',
    create: '/administracion/configuracionFel',
    update: '/administracion/configuracionFel',
    delete: '/administracion/configuracionFel',
  },
  user: {
    list: '/administracion/usuarios',
    create: '/administracion/usuarios',
    update: '/administracion/usuarios',
    delete: '/administracion/usuarios',
    changePassword: '/administracion/usuarios/cambiarContrasena',
  },
  rol: {
    list: '/administracion/roles',
    create: '/administracion/roles',
    update: '/administracion/roles',
    delete: '/administracion/roles',
  },
  companie: {
    list: '/administracion/empresas',
    create: '/administracion/empresas',
    update: '/administracion/empresas',
    delete: '/administracion/empresas',
  },
  relationRolService: {
    list: '/administracion/serviciosRol/consultarRegistrosProInd',
    create: '/administracion/serviciosRol/dinamicAccordion',
  },
  client: {
    list: `/clientes`,
    getPayments: (empresa, sucursal, fecha) => `/clientes/infoTabla/${empresa}/${sucursal}/${fecha}`,
    create: '/clientes',
    uploadFile: '/clientes/upload',
    getTransaction: `/clientes/revisarCortes`,
  },
  history: {
    list: `/historico`,
    getPayments: (empresa, sucursal, fecha, fechaFinal, estado) => `/historico/infoTabla/${empresa}/${sucursal}/${fecha}/${fechaFinal}/${estado}`,
  },
  datosGenerales: {
    getDatosGenerales: `/datosGenerales`,
    getDatosGeneralesDeptos: `/datosGenerales/departamentos`,
    getDatosGeneralesMunis: `/datosGenerales/municipios`,
    getDatosGeneralesEmpresas: `/datosGenerales/empresas`,
    getDatosGeneralesBancos: `/datosGenerales/bancos`,
  },
  organization: {
    list: '/administracion/organizaciones',
    create: '/administracion/organizaciones',
    update: '/administracion/organizaciones',
    delete: '/administracion/organizaciones'
  },
  onboarding: {
    // CRUD links (listado + creación + edición + borrado del asesor)
    linksList: '/onboarding/onboarding_links',
    linksListDetalle: '/onboarding/onboarding_links/detalles',
    linksCreate: '/onboarding/onboarding_links',
    linksUpdate: '/onboarding/onboarding_links',
    linksDelete: (id) => `/onboarding/onboarding_links/${id}`,
    linksDetail: (uuid) => `/onboarding/onboarding_links/${uuid}`,
    // Wizard endpoints
    status: (uuid) => `/onboarding/wizard/${uuid}/status`,
    validarIdentificacion: (uuid) => `/onboarding/wizard/${uuid}/validar-identificacion`,
    documentosRequeridos: (uuid) => `/onboarding/wizard/${uuid}/documentos-requeridos`,
    empresa: (uuid) => `/onboarding/wizard/${uuid}/empresa`,
    usuarios: (uuid) => `/onboarding/wizard/${uuid}/usuarios`,
    documentos: (uuid) => `/onboarding/wizard/${uuid}/documentos`,
    documentoDetalle: (uuid, idDocumento) => `/onboarding/wizard/${uuid}/documentos/${idDocumento}`,
    documentoDescargar: (uuid, idDocumento) => `/onboarding/wizard/${uuid}/documentos/${idDocumento}/descargar`,
    documentoTexto: (uuid) => `/onboarding/wizard/${uuid}/documentos-texto`,
    documentoAprobar: (uuid, idDocumento) => `/onboarding/wizard/${uuid}/documentos/${idDocumento}/aprobar`,
    documentoRechazar: (uuid, idDocumento) => `/onboarding/wizard/${uuid}/documentos/${idDocumento}/rechazar`,
    completarEtapa1: (uuid) => `/onboarding/wizard/${uuid}/etapa1/completar`,
    darDeAlta: (uuid) => `/onboarding/wizard/${uuid}/etapa1/dar-de-alta`,
    catalogoCuotas: (uuid) => `/onboarding/wizard/${uuid}/config/catalogo-cuotas`,
    catalogoMetodosPago: (uuid) => `/onboarding/wizard/${uuid}/config/catalogo-metodos-pago`,
    sucursalesEstado: (uuid) => `/onboarding/wizard/${uuid}/config/sucursales-estado`,
    configLogo: (uuid) => `/onboarding/wizard/${uuid}/config/logo`,
    configCajeros: (uuid) => `/onboarding/wizard/${uuid}/config/cajeros`,
    configMetodosPago: (uuid) => `/onboarding/wizard/${uuid}/config/metodos-pago`,
    configMetodoPagoDetalle: (uuid, idMetodo) => `/onboarding/wizard/${uuid}/config/metodos-pago/${idMetodo}`,
    configPoliticas: (uuid) => `/onboarding/wizard/${uuid}/config/politicas`,
    copiarSucursal: (uuid) => `/onboarding/wizard/${uuid}/config/copiar-sucursal`,
    completarEtapa2: (uuid) => `/onboarding/wizard/${uuid}/etapa2/completar`,
    activaciones: (uuid) => `/onboarding/wizard/${uuid}/activaciones`,
    activacionFelCarta: (uuid) => `/onboarding/wizard/${uuid}/activaciones/fel/carta`,
    activacionFelCartaDescargar: (uuid) => `/onboarding/wizard/${uuid}/activaciones/fel/carta/descargar`,
    activacionFelPlantillaDescargar: (uuid) => `/onboarding/wizard/${uuid}/activaciones/fel/carta-plantilla/descargar`,
    activacionFelPlantillaSubir: (uuid) => `/onboarding/wizard/${uuid}/activaciones/fel/plantilla`,
    activacionFelPlantillaEliminar: (uuid) => `/onboarding/wizard/${uuid}/activaciones/fel/plantilla`,
    activacionFelCartaAprobar: (uuid) => `/onboarding/wizard/${uuid}/activaciones/fel/carta/aprobar`,
    activacionFelCartaRechazar: (uuid) => `/onboarding/wizard/${uuid}/activaciones/fel/carta/rechazar`,
    activacionFelSolicitar: (uuid) => `/onboarding/wizard/${uuid}/activaciones/fel/solicitar`,
    activacionPagaloSolicitar: (uuid) => `/onboarding/wizard/${uuid}/activaciones/pagalo/solicitar`,
    configFelCredenciales: (uuid) => `/onboarding/wizard/${uuid}/config/fel-credenciales`,
    completarEtapa5: (uuid) => `/onboarding/wizard/${uuid}/etapa5/completar`,
    pruebasFel: (uuid) => `/onboarding/wizard/${uuid}/pruebas-fel`,
    pruebasFelProducto: (uuid) => `/onboarding/wizard/${uuid}/pruebas-fel/producto`,
    pruebasFelTest: (uuid) => `/onboarding/wizard/${uuid}/pruebas-fel/test`,
    pruebasFelProduccion: (uuid) => `/onboarding/wizard/${uuid}/pruebas-fel/produccion`,
    pruebasFelAnular: (uuid) => `/onboarding/wizard/${uuid}/pruebas-fel/anular`,
    pruebasFelPdf: (uuid) => `/onboarding/wizard/${uuid}/pruebas-fel/pdf`,
    configPagalo: (uuid) => `/onboarding/wizard/${uuid}/config/pagalo`,
  },
  licencia: {
    list: '/onboarding/licencias',
  },
  onboardingCatalogos: {
    licencias: '/onboarding/onboarding_catalogos/licencias',
    licenciaDetalle: (id) => `/onboarding/onboarding_catalogos/licencias/${id}/detalle`,
    licenciaPrecios: (id) => `/onboarding/onboarding_catalogos/licencias/${id}/precios`,
    temporalidades: '/onboarding/onboarding_catalogos/temporalidades',
  },
};
